import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import LandingPage from './components/LandingPage';
import ToolPage from './components/ToolPage';
import SavedReadings from './components/SavedReadings';
import AdminDashboard from './components/AdminDashboard';
import FamilyDashboard from './components/FamilyDashboard';
import ResetPasswordPage from './components/ResetPasswordPage';
import SharedReadingPage from './components/SharedReadingPage';
import FAQPage from './components/FAQPage';
import UniversalYearPage from './components/UniversalYearPage';
import { PrivacyPage, TermsPage } from './components/LegalPages';
import FeedbackWidget from './components/FeedbackWidget';
import AuthPage from './components/AuthPage';
import JoinPage from './components/JoinPage';
import FloatingReadingCta from './components/FloatingReadingCta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

export function getToken() { return localStorage.getItem('lbn_token'); }

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => localStorage.removeItem('lbn_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('lbn_token', data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem('lbn_token', data.token);
    setUser(data.user);
    return data.user;
  };

  // Account-lite sign-up — used by the reading-gate after the first free reading.
  // Captures name + email only; the user can later set a password via forgot-password.
  const quickRegister = async (name, email) => {
    const { data } = await axios.post(`${API}/auth/quick-register`, { name, email });
    localStorage.setItem('lbn_token', data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('lbn_token');
    setUser(null);
  };

  // Re-fetches the current user from the server. Called after profile
  // mutations (avatar upload, name change, founding-member toggle) so the
  // UI stays in sync with the database without a full page reload.
  const refreshUser = async () => {
    const token = getToken();
    if (!token) return null;
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, quickRegister, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// Primary logo — lowercase wordmark
function Logo({ onClick }) {
  return (
    <div className="lbn-logo" onClick={onClick} data-testid="logo">
      <div className="lbn-logo-wordmark">
        <div className="lbn-w-row">
          <span className="lbn-w-little">little</span>
          <span className="lbn-w-blue">blue</span>
          <span className="lbn-w-numbers">numbers</span>
        </div>
      </div>
    </div>
  );
}

function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 300);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  const openAuth = (tab) => {
    setMobileOpen(false);
    // Auth is now a real page (not a modal) so it works reliably on every
    // device, especially mobile where modals were getting clipped.
    navigate(tab === 'register' ? '/signup' : '/signin');
  };

  return (
    <nav className="lbn-nav">
      <Logo onClick={() => go('/')} />

      <button
        className="lbn-nav-toggle"
        onClick={() => setMobileOpen(v => !v)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
        data-testid="nav-toggle"
      >
        <span className="lbn-nav-toggle-icon">
          <span /><span /><span />
        </span>
      </button>

      <div className={`lbn-nav-links ${mobileOpen ? 'open' : ''}`}>
        <button className="nav-link" onClick={() => scrollTo('how-section')} data-testid="nav-how">How it works</button>
        <button className="nav-link nav-link-highlight" onClick={() => scrollTo('founding-section')} data-testid="nav-founding">Founding members</button>
        <button className="nav-link" onClick={() => go('/universal-year')} data-testid="nav-universal-year">{new Date().getFullYear()} Universal Year</button>
        <button className="nav-link" onClick={() => go('/faq')} data-testid="nav-faq">FAQ</button>
        <button className="nav-link" onClick={() => scrollTo('about-section')} data-testid="nav-about">About</button>
        {user ? (
          <>
            <button className="nav-link" onClick={() => go('/saved')} data-testid="nav-saved">Saved</button>
            <button className="nav-link" onClick={() => go('/family')} data-testid="nav-family">Family</button>
            {user.role === 'admin' && (
              <button className="nav-link" onClick={() => go('/admin')} data-testid="nav-admin">Admin</button>
            )}
            <button className="nav-link" onClick={() => { logout(); setMobileOpen(false); }} data-testid="nav-signout">Sign out</button>
          </>
        ) : (
          <>
            <button className="nav-link nav-link-accent" onClick={() => openAuth('login')} data-testid="nav-signin">Sign in</button>
            <button className="nav-link nav-link-accent" onClick={() => openAuth('register')} data-testid="nav-signup">Sign up</button>
          </>
        )}
        <button className="nav-btn" onClick={() => go('/tool')} data-testid="nav-get-reading">Get your reading</button>
      </div>
    </nav>
  );
}

function AppContent() {
  return (
    <>
      <Nav />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/tool" element={<ToolPage />} />
        <Route path="/signin" element={<AuthPage initialTab="login" />} />
        <Route path="/signup" element={<AuthPage initialTab="register" />} />
        <Route path="/join" element={<JoinPage />} />
        <Route path="/saved" element={<SavedReadings />} />
        <Route path="/family" element={<FamilyDashboard />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/r/:sessionId" element={<SharedReadingPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/universal-year" element={<UniversalYearPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
      <FeedbackWidget />
      <FloatingReadingCta />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppContent />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
